/**
 * Filter helpers: which responses enter official aggregation.
 *
 * Phase 1 rule (unchanged as the default): advisory Voice responses never
 * enter official math. Phase 2 (PRD §4, behind the voices_counted flag at
 * generation time): a response persisted with a counted policy SNAPSHOT
 * (tool_session_responses.counting_policy = 'counted', migration 601) is
 * included in official aggregation — always badged, never silent.
 *
 * The snapshot is what decides, not the seat's current policy: a Voice row
 * with no counted snapshot is advisory, so legacy data (persisted before
 * migration 601) and flag-off workspaces behave byte-identically to Phase 1.
 * Humans (principal_type = 'user') are always official; counting_policy is
 * meaningless on their rows.
 */

export type PrincipalTyped = {
  principalType?: string | null;
  principal_type?: string | null;
  voiceId?: string | null;
  voice_id?: string | null;
  countingPolicy?: string | null;
  counting_policy?: string | null;
};

export function isVoicePrincipal(row: PrincipalTyped): boolean {
  const t = row.principalType ?? row.principal_type;
  if (t === 'voice') return true;
  return Boolean(row.voiceId ?? row.voice_id);
}

/** True for Voice rows persisted under a counted policy snapshot (PRD §4). */
export function isCountedVoiceResponse(row: PrincipalTyped): boolean {
  if (!isVoicePrincipal(row)) return false;
  return (row.countingPolicy ?? row.counting_policy) === 'counted';
}

export interface VoiceResponsePartition<T> {
  /** Official math: humans + counted Voice responses (badged). */
  official: T[];
  /** Advisory Voice responses — context only, never official. */
  advisoryVoices: T[];
  /** Counted Voice responses — the badged subset of `official`. */
  countedVoices: T[];
}

/**
 * Partition responses for results views: humans are always official;
 * Voice rows split on their persisted counting-policy snapshot.
 */
export function partitionVoiceResponses<T extends PrincipalTyped>(
  rows: T[],
): VoiceResponsePartition<T> {
  const official: T[] = [];
  const advisoryVoices: T[] = [];
  const countedVoices: T[] = [];
  for (const row of rows) {
    if (!isVoicePrincipal(row)) {
      official.push(row);
    } else if (isCountedVoiceResponse(row)) {
      official.push(row);
      countedVoices.push(row);
    } else {
      advisoryVoices.push(row);
    }
  }
  return { official, advisoryVoices, countedVoices };
}

/**
 * Official math: humans always; Voice rows only with a counted snapshot.
 * When no counted snapshots exist (Phase 1 data, flag off) this is exactly
 * the Phase 1 "humans only" behavior.
 */
export function excludeAdvisoryVoices<T extends PrincipalTyped>(rows: T[]): T[] {
  return partitionVoiceResponses(rows).official;
}

/** Every Voice contribution (advisory + counted) for the results context view. */
export function onlyVoiceResponses<T extends PrincipalTyped>(rows: T[]): T[] {
  return rows.filter((r) => isVoicePrincipal(r));
}

/** Separable advisory Voice contributions (context only, not in official math). */
export function onlyAdvisoryVoices<T extends PrincipalTyped>(rows: T[]): T[] {
  return partitionVoiceResponses(rows).advisoryVoices;
}
